import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react'
import { authApi, type OAuthUser, type OAuthTokenResponse } from '../api/authApi'

// Refresh 5 minutes before the 30-min access token expires
const PROACTIVE_REFRESH_MS = 25 * 60 * 1000 // 25 minutes
const IDLE_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes — log out inactive users
const IDLE_CHECK_INTERVAL_MS = 60_000 // Check every 60 seconds

interface User {
  id?: string
  username?: string
  email?: string
  full_name?: string
  userType: 'customer' | 'broker' | 'admin' | 'data_operator'
  role?: 'customer' | 'broker'
  is_new_user?: boolean
}

interface AuthContextType {
  user: User | null
  login: (username: string, userType: 'customer' | 'broker' | 'admin' | 'data_operator') => void
  loginWithToken: (accessToken: string, user: OAuthUser, refreshToken?: string) => void
  logout: () => void
  loginOAuth: (data: OAuthTokenResponse) => void
  setRole: (role: 'customer' | 'broker') => Promise<void>
  refreshSession: () => Promise<boolean>
  isAuthenticated: boolean
  isLoading: boolean
  getAccessToken: () => string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const lastActivityRef = useRef<number>(Date.now())

  const mapOAuthToUser = (u: OAuthUser): User => ({
    id: u.id,
    email: u.email,
    full_name: u.full_name,
    userType: (u.role === 'admin' ? 'admin' : u.role === 'data_operator' ? 'data_operator' : u.role === 'broker' ? 'broker' : 'customer') as 'customer' | 'broker' | 'admin' | 'data_operator',
    role: (u.role === 'admin' || u.role === 'data_operator') ? undefined : u.role,
    is_new_user: u.is_new_user,
  })

  const refreshSession = useCallback(async (): Promise<boolean> => {
    const refresh = authApi.getRefreshToken()
    if (!refresh) return false
    try {
      const data = await authApi.refreshToken(refresh)
      authApi.saveTokens(data)
      setUser(mapOAuthToUser(data.user))
      return true
    } catch {
      authApi.clearTokens()
      setUser(null)
      return false
    }
  }, [])

  useEffect(() => {
    const init = async () => {
      const oauthUser = authApi.getUser()
      const access = authApi.getAccessToken()
      const refresh = authApi.getRefreshToken()

      if (oauthUser && access) {
        setUser(mapOAuthToUser(oauthUser))
        setIsLoading(false)
        return
      }

      if (refresh && !access) {
        try {
          const data = await authApi.refreshToken(refresh)
          authApi.saveTokens(data)
          setUser(mapOAuthToUser(data.user))
        } catch {
          authApi.clearTokens()
        }
      }

      const legacy = localStorage.getItem('loanloop_user')
      if (legacy) {
        try {
          const parsed = JSON.parse(legacy)
          if (parsed.username && parsed.userType) {
            setUser({
              username: parsed.username,
              userType: parsed.userType,
            })
          }
        } catch {
          localStorage.removeItem('loanloop_user')
        }
      }
      setIsLoading(false)
    }
    init()
  }, [])

  const login = (username: string, userType: 'customer' | 'broker' | 'admin' | 'data_operator') => {
    const userData = { username, userType }
    setUser(userData)
    localStorage.setItem('loanloop_user', JSON.stringify(userData))
  }

  const loginWithToken = (accessToken: string, userData: OAuthUser, refreshToken?: string) => {
    authApi.saveTokens({ access_token: accessToken, user: userData, refresh_token: refreshToken })
    setUser(mapOAuthToUser(userData))
  }

  const loginOAuth = (data: OAuthTokenResponse) => {
    authApi.saveTokens(data)
    setUser(mapOAuthToUser(data.user))
  }

  const logout = useCallback(() => {
    setUser(null)
    authApi.clearTokens()
    localStorage.removeItem('loanloop_user')
  }, [])

  // Listen for session-expired events dispatched by authenticatedFetch
  useEffect(() => {
    const handleSessionExpired = () => {
      const currentUser = authApi.getUser()
      const role = currentUser?.role
      logout()
      // Redirect to the appropriate login page with expired flag
      if (role === 'admin' || role === 'data_operator') {
        window.location.href = '/admin?expired=true'
      } else if (role === 'broker') {
        window.location.href = '/broker?expired=true'
      } else {
        window.location.href = '/login?expired=true'
      }
    }
    window.addEventListener('session-expired', handleSessionExpired)
    return () => window.removeEventListener('session-expired', handleSessionExpired)
  }, [logout])

  // Track user activity for idle timeout
  useEffect(() => {
    const updateActivity = () => { lastActivityRef.current = Date.now() }
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
    events.forEach(e => window.addEventListener(e, updateActivity, { passive: true }))
    return () => { events.forEach(e => window.removeEventListener(e, updateActivity)) }
  }, [])

  // Idle timeout — log out users after 30 minutes of inactivity
  const idleTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (user) {
      idleTimerRef.current = setInterval(() => {
        if (Date.now() - lastActivityRef.current > IDLE_TIMEOUT_MS) {
          // Read user data BEFORE logout clears it
          const currentUser = authApi.getUser()
          const role = currentUser?.role
          logout()
          if (role === 'admin' || role === 'data_operator') {
            window.location.href = '/admin?expired=true'
          } else if (role === 'broker') {
            window.location.href = '/broker?expired=true'
          } else {
            window.location.href = '/login?expired=true'
          }
        }
      }, IDLE_CHECK_INTERVAL_MS)
    }
    return () => { if (idleTimerRef.current) clearInterval(idleTimerRef.current) }
  }, [user, logout])

  // Proactive token refresh — refresh before the access token expires
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const startRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    const refreshToken = authApi.getRefreshToken()
    if (!refreshToken) return
    refreshTimerRef.current = setTimeout(async () => {
      // Only refresh if user was active recently
      if (Date.now() - lastActivityRef.current < IDLE_TIMEOUT_MS) {
        const success = await refreshSession()
        if (success) startRefreshTimer() // Schedule next refresh
      }
      // If idle, don't refresh — idle check interval will handle logout
    }, PROACTIVE_REFRESH_MS)
  }, [refreshSession])

  // Start timer when user is authenticated
  useEffect(() => {
    if (user && authApi.getRefreshToken()) {
      startRefreshTimer()
    }
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    }
  }, [user, startRefreshTimer])

  const setRole = async (role: 'customer' | 'broker') => {
    const token = authApi.getAccessToken()
    if (!token) throw new Error('Not authenticated')
    await authApi.setRole(role, token)
    const u = authApi.getUser()
    if (u) {
      localStorage.setItem('loanloop_user', JSON.stringify({ ...u, role }))
      setUser(mapOAuthToUser({ ...u, role }))
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        loginWithToken,
        logout,
        loginOAuth,
        setRole,
        refreshSession,
        isAuthenticated: !!user,
        isLoading,
        getAccessToken: authApi.getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
